import React from 'react';
import { debounce } from '@mui/material/utils';

interface ScrollInfo {
  isScrolledPast: boolean;
  isScrollingDown: boolean;
  elementDistance: number; // distance from top of viewport to element
}

export const useScrollPastElement = (
  elementRef: React.RefObject<HTMLElement>
) => {
  const [scrollInfo, setScrollInfo] = React.useState<ScrollInfo>({
    isScrolledPast: false,
    isScrollingDown: false,
    elementDistance: 0,
  });

  React.useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = debounce(() => {
      const currentScrollY = window.scrollY;

      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const elementDistance = rect.top;

        // Only update state if values have changed
        setScrollInfo((prevState) => {
          const newIsScrolledPast = elementDistance < 0;
          const newIsScrollingDown = currentScrollY > lastScrollY;

          if (
            prevState.isScrolledPast !== newIsScrolledPast ||
            prevState.isScrollingDown !== newIsScrollingDown ||
            prevState.elementDistance !== elementDistance
          ) {
            return {
              isScrolledPast: newIsScrolledPast,
              isScrollingDown: newIsScrollingDown,
              elementDistance,
            };
          }
          return prevState;
        });
      }

      lastScrollY = currentScrollY;
    }, 10);

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.clear();
    };
  }, [elementRef]);

  return scrollInfo;
};
